// src/data/renewalDecisionsClient.js
//
// P-15: LEASE-RENEWAL-DECISION-REGISTER
//
// Three exports:
//   logRenewalDecision(locId, leaseId, payload)  — POST per-lease decision
//   getLeaseDecisions(locId, leaseId)             — GET per-lease list
//   getPortfolioDecisions()                       — GET portfolio register

import { AuthError, NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

// ─── Internal fetch helper ────────────────────────────────────────────────────

async function jsonRequest(url, options = {}) {
  let res;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
  } catch (err) {
    log.error("renewalDecisionsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }

  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("renewalDecisionsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }

  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── Per-lease decision: POST ─────────────────────────────────────────────────

/**
 * Log a renewal decision for a specific lease.
 *
 * @param {string} locId
 * @param {string} leaseId
 * @param {object} payload  — { disposition, ownerName?, ownerRole?, targetDate?,
 *                              rationale?, expectedAction?, status?, workflowLink? }
 * @returns {Promise<object>}  The created decision row (camelCase).
 */
export async function logRenewalDecision(locId, leaseId, payload) {
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locId)}/leases/${encodeURIComponent(leaseId)}/renewal-decisions`,
    {
      method: "POST",
      body:   JSON.stringify(payload),
    },
  );
}

// ─── Per-lease decision: GET ──────────────────────────────────────────────────

/**
 * Fetch all non-deleted decisions for a specific lease, newest first.
 *
 * @param {string} locId
 * @param {string} leaseId
 * @returns {Promise<Array>}
 */
export async function getLeaseDecisions(locId, leaseId) {
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locId)}/leases/${encodeURIComponent(leaseId)}/renewal-decisions`,
  );
}

// ─── Portfolio register: GET ──────────────────────────────────────────────────

/**
 * Fetch the portfolio-wide decisions register.
 * Includes locationAlias, locationMarket, and all decision fields.
 *
 * @returns {Promise<Array>}
 */
export async function getPortfolioDecisions() {
  return jsonRequest("/api/decisions/renewal");
}
